import { Button, Grid } from "@mui/material";
import { useCallback, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import FilterTable from "../../components/table/FilterTable";
import { useDeleteDraftMutation } from "../../services/draftsService";
import TableService from "../../services/tableService";
import { EmailTemplateAddDialog } from "./EmailTemplates/EmailTemplateAddDialog";
import { EmailTemplateEditDialog } from "./EmailTemplates/EmailTemplateEditDialog";
import {
    SALES_DRAFTS_TABLE_NAME,
    useSalesDraftsTableFieldsConfig,
} from "./SalesDraftsTableFieldsConfig";

export function EmailTemplatesTab({
    dataLocal,
    setDataLocal,
    recordsCount,
    resetPageNumber,
    selectedRowId,
    setSelectedRowId,
    hiddenColumnsForTables,
    columnsOrdersForTables,
    checkedAll,
    setCheckedAll,
    dataPage,
    handleChangeDataPage,
    dataPageSize,
    handleChangeDataPageSize,
    searchParams,
}) {
    const { t } = useTranslation();
    const SALES_DRAFTS_TABLE_FIELDS_CONFIG = useSalesDraftsTableFieldsConfig();

    const [addTemplateDialogOpen, setAddTemplateDialogOpen] = useState(false);
    const [editTemplateDialogOpen, setEditTemplateDialogOpen] = useState(false);
    const [selectedTemplateForEdit, setSelectedTemplateForEdit] = useState(null);

    const deleteDraftMutation = useDeleteDraftMutation({
        onSuccess: () => {
            toast.success(t("drafts.deleteSuccess"));
        },
        onError: () => {
            toast.error(t("drafts.deleteError"));
        },
    });

    const onClickButton = useCallback(
        (e, id, action) => {
            e.stopPropagation();
            if (action === "edit") {
                const template = dataLocal.find((t) => t.id === id);
                if (template) {
                    setSelectedTemplateForEdit(template);
                    setEditTemplateDialogOpen(true);
                }
            }
            if (action === "delete") {
                deleteDraftMutation.mutate({ draftId: id });
            }
        },
        [dataLocal, deleteDraftMutation],
    );

    const templatesDataExportHeaders = useMemo(() => {
        return TableService.getTableExportHeaders(
            SALES_DRAFTS_TABLE_FIELDS_CONFIG,
            Array.isArray(hiddenColumnsForTables?.[SALES_DRAFTS_TABLE_NAME])
                ? hiddenColumnsForTables[SALES_DRAFTS_TABLE_NAME]
                : [],
            Array.isArray(columnsOrdersForTables?.[SALES_DRAFTS_TABLE_NAME])
                ? columnsOrdersForTables[SALES_DRAFTS_TABLE_NAME]
                : [],
        );
    }, [SALES_DRAFTS_TABLE_FIELDS_CONFIG, hiddenColumnsForTables, columnsOrdersForTables]);

    const data = useMemo(
        () =>
            TableService.getPreparedDataForCollapsibleTable(
                SALES_DRAFTS_TABLE_FIELDS_CONFIG,
                dataLocal,
                Array.isArray(hiddenColumnsForTables?.[SALES_DRAFTS_TABLE_NAME])
                    ? hiddenColumnsForTables[SALES_DRAFTS_TABLE_NAME]
                    : [],
                Array.isArray(columnsOrdersForTables?.[SALES_DRAFTS_TABLE_NAME])
                    ? columnsOrdersForTables[SALES_DRAFTS_TABLE_NAME]
                    : [],
                onClickButton,
            ),
        [
            SALES_DRAFTS_TABLE_FIELDS_CONFIG,
            dataLocal,
            hiddenColumnsForTables,
            columnsOrdersForTables,
            onClickButton,
        ],
    );

    return (
        <>
            <Grid container spacing={10} justifyContent="left" sx={{ mb: 2 }}>
                <Grid item>
                    <Button variant="contained" onClick={() => setAddTemplateDialogOpen(true)}>
                        {t("drafts.emailTemplates.addTemplate")}
                    </Button>
                </Grid>
            </Grid>
            <FilterTable
                //@ts-ignore
                data={data}
                countRecords={recordsCount}
                resetPageNumber={resetPageNumber}
                selectedItemId={selectedRowId}
                style={{ maxHeight: "60vh" }}
                headersConfig={TableService.orderColumns(
                    TableService.hideHiddenColumns(
                        SALES_DRAFTS_TABLE_FIELDS_CONFIG,
                        Array.isArray(hiddenColumnsForTables?.[SALES_DRAFTS_TABLE_NAME])
                            ? hiddenColumnsForTables[SALES_DRAFTS_TABLE_NAME]
                            : [],
                    ),
                    Array.isArray(columnsOrdersForTables?.[SALES_DRAFTS_TABLE_NAME])
                        ? columnsOrdersForTables[SALES_DRAFTS_TABLE_NAME]
                        : [],
                )}
                showCleanFilterIcon={false}
                showContextMenu={false}
                showExportToFileButton={false}
                exportToFileUrl={"email-templates/export/"}
                exportToFileSearchParams={searchParams}
                exportToFileHeaders={templatesDataExportHeaders}
                exportToFileFileName={`${t("drafts.emailTemplates.exportFileName")}.xlsx`}
                showDetailsIcon={false}
                showCheckbox={false}
                checkedAll={checkedAll}
                page={dataPage}
                onPageChange={handleChangeDataPage}
                rowsPerPage={dataPageSize}
                onRowsPerPageChange={handleChangeDataPageSize}
                setDataLocal={setDataLocal}
                setCheckedAll={setCheckedAll}
                setSelectedRowId={setSelectedRowId}
                showCounterRecords={false}
                hideFiltersIconCol={true}
            />
            <EmailTemplateAddDialog
                title={t("drafts.emailTemplates.addTemplate")}
                open={addTemplateDialogOpen}
                onClose={() => setAddTemplateDialogOpen(false)}
            />
            <EmailTemplateEditDialog
                title={t("drafts.emailTemplates.editTemplate")}
                open={editTemplateDialogOpen}
                onClose={() => {
                    setEditTemplateDialogOpen(false);
                    setSelectedTemplateForEdit(null);
                }}
                //@ts-ignore
                template={selectedTemplateForEdit}
                onSuccessUpdate={() => {}}
            />
        </>
    );
}
